/*
  Format of the freight cost xlsx file:
  the factory freight costs are exported into it, and the edited file can be uploaded back.
  Every active factory has a sheet named after its identifier.
  The first column holds the distance ranges,
  every other column the freight costs of one freight cost type.
  The types and their order come from the config of the mode, so the file is mode independent.
*/

import { FreightCostTypes } from '../../types/freightCost/freightCostType';
import { XlsxCellValue, XlsxRow } from '../../types/xlsx';
import { isEmptyCell } from '../xlsx';

/** Index of the row holding the headers */
export const HEADER_ROW_INDEX = 0;

/** Index of the column holding the distance ranges */
export const DISTANCE_COLUMN_INDEX = 0;

/** Label of the "over the ranges" row in the distance column */
export const OVER_RANGES_LABEL = '>';

/** Header of the distance column */
export const DISTANCE_COLUMN_HEADER = 'Km távolság';

/**
 * Header of a freight cost type's column.
 * The config key (the stored value) is not written into the file,
 * so reading a file resolves the types by this.
 *
 * @param {FreightCostTypes} freightCostTypes the freight cost types of the mode
 * @param {string} type the config key of the type
 * @returns {string} the header of the type's column
 */
export function getFreightCostTypeHeader(freightCostTypes: FreightCostTypes, type: string): string {
  return freightCostTypes[type]?.shortTranslation || type;
}

/**
 * The headers of every freight cost type, in the order of the columns.
 *
 * @param {FreightCostTypes} freightCostTypes the freight cost types
 * @returns {string[]} the header of every type
 */
export function getFreightCostTypeHeaders(freightCostTypes: FreightCostTypes): string[] {
  return Object.keys(freightCostTypes).map((type) =>
    getFreightCostTypeHeader(freightCostTypes, type)
  );
}

/**
 * The freight cost type of a column header.
 * Only the surrounding spaces are ignored: the header has to be written as it stored,
 * so that a mistyped one is reported as an error instead of being guessed.
 *
 * @param {string} header the header cell of the column
 * @param {FreightCostTypes} freightCostTypes the freight cost types
 * @returns {string | undefined} the key of the type, or undefined when no type has this header
 */
export function resolveFreightCostType(
  header: string,
  freightCostTypes: FreightCostTypes
): string | undefined {
  const trimmedHeader = header.trim();

  return Object.keys(freightCostTypes).find(
    (type) => getFreightCostTypeHeader(freightCostTypes, type).trim() === trimmedHeader
  );
}

/**
 * Whether a cell of the distance column marks the "over the ranges" row
 * instead of holding a distance.
 *
 * @param {XlsxCellValue} value the value of the cell
 * @returns {boolean} whether the cell holds the "over the ranges" label
 */
export function isOverRangesLabel(value: XlsxCellValue): boolean {
  return typeof value === 'string' && value.trim() === OVER_RANGES_LABEL;
}

/**
 * Whether a sheet is headed the way the format wants it.
 *
 * @param {XlsxRow | undefined} headerRow the first row of the sheet
 * @returns {boolean} whether its first cell is the header of the distance column
 */
export function hasDistanceHeader(headerRow: XlsxRow | undefined): boolean {
  const header = headerRow?.[DISTANCE_COLUMN_INDEX];

  return (
    !isEmptyCell(header) &&
    String(header).trim().toLocaleLowerCase('hu') === DISTANCE_COLUMN_HEADER.toLocaleLowerCase('hu')
  );
}
